import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Phone, 
  Mail, 
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
  ArrowRight,
  Clock,
  Award
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const projects = [
    { name: 'MAA Srinivasam', path: '/Projects/srinivas', type: '2 & 3 BHK Apartments' },
    { name: 'MAA Garudadri', path: '/Projects/garudadri', type: 'Premium Villa Plots' }
  ];

  const socialLinks = [
    { icon: <Facebook className="w-5 h-5" />, url: '#', name: 'Facebook' },
    { icon: <Instagram className="w-5 h-5" />, url: '#', name: 'Instagram' },
    { icon: <Linkedin className="w-5 h-5" />, url: '#', name: 'LinkedIn' },
    { icon: <Youtube className="w-5 h-5" />, url: '#', name: 'YouTube' },
    { icon: <Twitter className="w-5 h-5" />, url: '#', name: 'Twitter' }
  ];

  return (
    <footer className="bg-gradient-to-b from-[#2d1f10] to-[#1a1208] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6 group">
              <div className="bg-gradient-to-br from-[#8B7355] to-[#6B5744] text-white p-3 rounded-lg group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Building2 className="w-7 h-7" />
              </div>
              <span className="text-3xl font-bold">Vedamsa</span>
            </Link>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Building Dreams, Creating Futures. Turn your dream of owning a home into reality with Vedamsa Infra.
            </p>
            
            {/* Trust Badge */}
            <div className="flex items-start space-x-3 p-4 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 mb-6">
              <Award className="w-6 h-6 text-[#8B7355] flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white mb-1">30+ Years Legacy</p>
                <p className="text-sm text-gray-400">3000+ Happy Customers</p>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <p className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">Follow Us</p>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 hover:bg-[#8B7355] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg group"
                    aria-label={social.name}
                  >
                    <span className="group-hover:scale-110 transition-transform duration-300">
                      {social.icon}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 relative inline-block">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-[#8B7355] to-transparent"></span>
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-[#8B7355] transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy-policy" className="text-gray-300 hover:text-[#8B7355] transition-colors duration-300 flex items-center group">
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">Privacy Policy</span>
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-[#8B7355] transition-colors duration-300 flex items-center group">
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">Terms & Conditions</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Our Projects */}
          <div>
            <h3 className="text-xl font-bold mb-6 relative inline-block">
              Our Projects
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-[#8B7355] to-transparent"></span>
            </h3>
            <ul className="space-y-4">
              {projects.map((project, index) => (
                <li key={index}>
                  <Link 
                    to={project.path}
                    className="block p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-300 border border-white/5 hover:border-[#8B7355]/30 group"
                  >
                    <h4 className="font-semibold text-white mb-1 group-hover:text-[#8B7355] transition-colors duration-300">
                      {project.name}
                    </h4>
                    <p className="text-sm text-gray-400">{project.type}</p>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 p-4 bg-gradient-to-r from-[#8B7355]/20 to-transparent rounded-lg border border-[#8B7355]/30">
              <p className="text-sm text-gray-300 mb-2">Interested in our projects?</p>
              <Link 
                to="/contact"
                className="inline-flex items-center space-x-2 text-[#8B7355] hover:text-white font-semibold transition-colors duration-300 group"
              >
                <span>Schedule a Visit</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6 relative inline-block">
              Get In Touch
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-[#8B7355] to-transparent"></span>
            </h3>
            
            <div className="space-y-4">
              {/* Phone */}
              <a 
                href="tel:8885852266"
                className="flex items-start space-x-3 p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-300 group border border-white/5 hover:border-[#8B7355]/30"
              >
                <div className="w-10 h-10 bg-[#8B7355]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#8B7355] transition-colors duration-300">
                  <Phone className="w-5 h-5 text-[#8B7355] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Phone</p>
                  <p className="text-white font-semibold group-hover:text-[#8B7355] transition-colors duration-300">
                    8885852266
                  </p>
                </div>
              </a>

              {/* Email */}
              <a 
                href="mailto:Vedamsa@gmail.com"
                className="flex items-start space-x-3 p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-300 group border border-white/5 hover:border-[#8B7355]/30"
              >
                <div className="w-10 h-10 bg-[#8B7355]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#8B7355] transition-colors duration-300">
                  <Mail className="w-5 h-5 text-[#8B7355] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Email</p>
                  <p className="text-white font-semibold group-hover:text-[#8B7355] transition-colors duration-300 break-all">
                    Vedamsa@gmail.com
                  </p>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-lg border border-white/5">
                <div className="w-10 h-10 bg-[#8B7355]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#8B7355]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Location</p>
                  <p className="text-white font-semibold">
                    Hyderabad, Telangana, India
                  </p>
                </div>
              </div>

              {/* Business Hours */}
              <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-lg border border-white/5">
                <div className="w-10 h-10 bg-[#8B7355]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#8B7355]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Business Hours</p>
                  <p className="text-white font-semibold">Mon - Sat: 9AM - 6PM</p>
                  <p className="text-gray-400 text-sm">Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="py-8 border-t border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
              <p className="text-gray-400">Subscribe to our newsletter for latest updates and exclusive offers</p>
            </div>
            <div>
              <form className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-white placeholder-gray-400"
                />
                <button 
                  type="submit"
                  className="bg-gradient-to-r from-[#8B7355] to-[#6B5744] text-white px-8 py-3 rounded-lg font-semibold hover:from-[#6B5744] hover:to-[#8B7355] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © {currentYear} Vedamsa Infrastructure Pvt Ltd. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Crafted with excellence for your dream home
              </p>
            </div>
            
            <div className="flex items-center space-x-6">
              <Link to="/sitemap" className="text-gray-400 hover:text-[#8B7355] text-sm transition-colors duration-300">
                Sitemap
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/privacy-policy" className="text-gray-400 hover:text-[#8B7355] text-sm transition-colors duration-300">
                Privacy
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms" className="text-gray-400 hover:text-[#8B7355] text-sm transition-colors duration-300">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Wave */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#8B7355] to-transparent"></div>
    </footer>
  );
};

export default Footer;